import { makeAutoObservable } from "mobx";
import RootStore from "../../../../../stores/RootStore";
import ServiceStore from "../../../../../stores/ServiceStore";
import {
  Opportunity,
  Account,
  Contact,
  Activity,
  Bid
} from "types/common/ServiceStoreTypes";
import { Job as Project } from "types/common/ServiceStoreTypes";
import { OpportunitiesListStore } from "../../OpportunitiesList.Store";

export class OpportunityDetailStore {
  rootStore: RootStore;

  // Observable state
  opportunity: Opportunity | null = null;
  contact: Contact | null = null;
  activities: Activity[] = [];
  loading = false;
  error: string | null = null;
  activeTab = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Service store access
  get opportunitiesStore(): ServiceStore {
    return this.rootStore.opportunitiesStore as ServiceStore;
  }

  get accountsStore(): ServiceStore {
    return this.rootStore.accountsStore as ServiceStore;
  }

  get contactsStore(): ServiceStore {
    return this.rootStore.contactsStore as ServiceStore;
  }

  get activitiesStore(): ServiceStore {
    return this.rootStore.activitiesStore as ServiceStore;
  }

  get bidsStore(): ServiceStore {
    return this.rootStore.bidsStore as ServiceStore;
  }

  get jobStore(): ServiceStore {
    return this.rootStore.jobStore as ServiceStore;
  }

  // Data loading methods
  async loadOpportunity(opportunityId: number) {
    this.loading = true;
    this.error = null;

    try {
      // First load all base entities from ServiceStores to ensure relational data is available
      await Promise.all([
        // Load all base entities without queries to populate stores
        this.opportunitiesStore.find(),
        this.accountsStore.find(),
        this.contactsStore.find(),
        this.activitiesStore.find(),
      ]);

      // Then load specific opportunity details
      const opportunity = (await this.opportunitiesStore.get(
        opportunityId
      )) as unknown as Opportunity;
      this.opportunity = opportunity;

      // Load related data in parallel (account is now computed)
      await Promise.all([
        this.loadContact(opportunity.contactId),
        this.loadActivities(opportunityId),
      ]);
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to load opportunity";
      console.error("Failed to load opportunity:", error);
    } finally {
      this.loading = false;
    }
  }

  async loadContact(contactId?: number) {
    if (!contactId) return;

    try {
      const contact = (await this.contactsStore.get(
        contactId
      )) as unknown as Contact;
      this.contact = contact;
    } catch (error) {
      console.error("Failed to load contact:", error);
    }
  }

  async loadActivities(opportunityId: number) {
    try {
      const response = await this.activitiesStore.find({
        query: {
          opportunityId: opportunityId,
          $sort: { CreatedAt: -1 },
          $limit: 50,
        },
      });
      this.activities = response?.data as unknown as Activity[];
    } catch (error) {
      console.error("Failed to load activities:", error);
    }
  }

  // Opportunity management methods
  async updateOpportunity(updates: Partial<Opportunity>) {
    if (!this.opportunity) return;

    try {
      const updated = (await this.opportunitiesStore.patch(
        this.opportunity.opportunityId,
        {
          ...updates,
          UpdatedAt: new Date().toISOString(),
        }
      )) as unknown as Opportunity;

      this.opportunity = { ...this.opportunity, ...updated };
      return updated;
    } catch (error) {
      console.error("Failed to update opportunity:", error);
      throw error;
    }
  }

  async deleteOpportunity() {
    if (!this.opportunity) return;

    try {
      await this.opportunitiesStore.remove(this.opportunity.opportunityId);
      this.opportunity = null;
    } catch (error) {
      console.error("Failed to delete opportunity:", error);
      throw error;
    }
  }

  // Quick stage updates
  async updateStage(newStage: string) {
    if (!this.opportunity) return;

    try {
      await this.updateOpportunity({ salesStage: newStage });
    } catch (error) {
      console.error("Failed to update stage:", error);
      throw error;
    }
  }

  async updateProbability(newProbability: number) {
    if (!this.opportunity) return;

    try {
      await this.updateOpportunity({ winProbability: newProbability });
    } catch (error) {
      console.error("Failed to update probability:", error);
      throw error;
    }
  }

  async updateValue(newValue: number) {
    if (!this.opportunity) return;

    try {
      await this.updateOpportunity({ estimatedValue: newValue });
    } catch (error) {
      console.error("Failed to update value:", error);
      throw error;
    }
  }

  async updateCloseDate(newDate: string) {
    if (!this.opportunity) return;

    try {
      await this.updateOpportunity({ expectedCloseDate: newDate });
    } catch (error) {
      console.error("Failed to update close date:", error);
      throw error;
    }
  }

  // Activity management
  async createActivity(activityData: Partial<Activity>) {
    if (!this.opportunity) return;

    try {
      const activity = (await this.activitiesStore.create({
        ...activityData,
        opportunityId: this.opportunity.opportunityId,
        accountId: this.opportunity.accountId,
        contactId: this.opportunity.contactId,
        CreatedAt: new Date().toISOString(),
        UpdatedAt: new Date().toISOString(),
      })) as unknown as Activity;

      this.activities.unshift(activity); // Add to beginning for chronological order
      return activity;
    } catch (error) {
      console.error("Failed to create activity:", error);
      throw error;
    }
  }

  // UI state management
  setActiveTab(tabIndex: number) {
    this.activeTab = tabIndex;
  }

  // Computed getters
  get stageProgress(): number {
    if (!this.opportunity) return 0;

    const stageOrder = [
      "Qualification",
      "Needs Analysis",
      "Value Proposition",
      "Id. Decision Makers",
      "Perception Analysis",
      "Proposal/Price Quote",
      "Negotiation/Review",
      "Closed Won",
    ];

    const currentIndex = stageOrder.indexOf(this.opportunity.salesStage);
    return currentIndex >= 0
      ? ((currentIndex + 1) / stageOrder.length) * 100
      : 0;
  }

  get isWon(): boolean {
    return this.opportunity?.salesStage === "Closed Won";
  }

  get isLost(): boolean {
    return this.opportunity?.salesStage === "Closed Lost";
  }

  get isClosed(): boolean {
    return this.isWon || this.isLost;
  }

  get daysToClose(): number | null {
    if (!this.opportunity?.expectedCloseDate) return null;

    const closeDate = new Date(this.opportunity.expectedCloseDate || Date.now());
    const today = new Date();
    const diffTime = closeDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  get recentActivities(): Activity[] {
    return this.activities.slice(0, 10);
  }

  get weightedValue(): number {
    if (!this.opportunity) return 0;
    const value = this.opportunity.estimatedValue || 0;
    const probability = (this.opportunity.winProbability || 0) / 100;
    return value * probability;
  }

  // Computed property for dialog opportunity
  get dialogOpportunity(): Opportunity | null {
    const opportunitiesListStore = this.rootStore
      .opportunitiesListStore as OpportunitiesListStore;
    const opportunityId = Number(
      this.rootStore.routerStore?.queryParams.dialogContext
    );

    if (!opportunityId || !opportunitiesListStore?.opportunities) {
      return null;
    }

    return (
      opportunitiesListStore.opportunities.find(
        (opportunity: Opportunity) =>
          opportunity.opportunityId === opportunityId
      ) || null
    );
  }

  // Computed property for account
  get account(): Account | null {
    if (!this.dialogOpportunity?.accountId) return null;

    const accountsStore = this.rootStore.accountsStore as ServiceStore;
    const account = accountsStore.entities.find((acc: unknown) => {
      const accountData = acc as { accountId: number };
      return accountData.accountId === this.dialogOpportunity!.accountId;
    });

    return account ? (account as unknown as Account) : null;
  }

  // Computed property for related bids
  get bids(): Bid[] {
    if (!this.dialogOpportunity?.opportunityId) return [];

    const bidsStore = this.rootStore.bidsStore as ServiceStore;
    return bidsStore.entities.filter((bid: unknown) => {
      const bidData = bid as { opportunityId: number };
      return bidData.opportunityId === this.dialogOpportunity!.opportunityId;
    }) as unknown as Bid[];
  }

  // Computed property for related projects
  get projects(): Project[] {
    if (!this.dialogOpportunity?.opportunityId) return [];

    const jobStore = this.rootStore.jobStore as ServiceStore;
    return jobStore.entities.filter((project: unknown) => {
      const projectData = project as { opportunityId: number };
      return (
        projectData.opportunityId === this.dialogOpportunity!.opportunityId
      );
    }) as unknown as Project[];
  }
}

export default OpportunityDetailStore;
